import React from 'react';
import { Box, Typography, Paper, Grid, Divider, Chip } from '@mui/material';
import { SwapVert } from '@mui/icons-material';
import { t } from '../i18n';
import {
  formatPostal,
  japaneseFull,
  englishFull,
  PLACEHOLDER_JA,
  PLACEHOLDER_EN,
} from '../utils/address';

// A real lookup result (100-0001) used as the worked example below.
const EXAMPLE = {
  postal_code: '1000001',
  kanji: { prefecture: '東京都', city: '', ward: '千代田区', town: '千代田', block: '', detail: '', note: '' },
  romaji: { prefecture: 'Tokyo', city: '', ward: 'Chiyoda-Ku', town: 'Chiyoda', block: '', detail: '', note: '' },
  is_building: false,
};

// The same address, split into ordered parts for each language. Japanese runs
// largest → smallest; English is the reverse.
const JA_PARTS = [
  [t('Postal code', '郵便番号'), '〒100-0001'],
  [t('Prefecture (都道府県)', '都道府県'), '東京都'],
  [t('Ward (区)', '区'), '千代田区'],
  [t('Town (町域)', '町域'), '千代田'],
  [t('Block / building (you add)', '番地・建物名（自分で追加）'), PLACEHOLDER_JA],
];
const EN_PARTS = [
  [t('Street / building (you add)', 'street / building（自分で追加）'), PLACEHOLDER_EN],
  [t('Town', '町域'), 'Chiyoda'],
  [t('Ward', '区'), 'Chiyoda-Ku'],
  [t('Prefecture', '都道府県'), 'Tokyo'],
  [t('Postal code', '郵便番号'), '100-0001'],
  [t('Country', '国'), 'Japan'],
];

const PartList = ({ heading, caption, parts }) => (
  <Paper
    elevation={0}
    sx={{ p: 2, height: '100%', bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
  >
    <Typography variant="subtitle1" fontWeight="bold">{heading}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{caption}</Typography>
    {parts.map(([label, value], i) => (
      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ textAlign: 'right' }}>{value}</Typography>
      </Box>
    ))}
  </Paper>
);

// Educational intro shown on the Postal Code page before any search: how a
// Japanese address is written and how it maps to English.
const AddressGuide = () => (
  <Box sx={{ mt: 2 }}>
    <Typography variant="h6" gutterBottom>
      {t('How a Japanese address works', '日本の住所の仕組み')}
    </Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      {t(
        'A Japanese address is written from the largest area to the smallest: postal code, prefecture, city/ward, town, then block and building. English writes it the other way around — smallest first — and adds “Japan” at the end. Look up a postal code above to fill in everything except your block/building number.',
        '日本の住所は大きい単位から小さい単位へと書きます。郵便番号 → 都道府県 → 市区町村 → 町域 → 番地・建物の順です。英語ではこの順序が逆になり（小さい単位から）、最後に「Japan」を付けます。上で郵便番号を調べると、番地・建物名以外がすべて埋まります。'
      )}
    </Typography>

    <Grid container spacing={2} alignItems="stretch" sx={{ mb: 1 }}>
      <Grid item xs={12} md={6}>
        <PartList
          heading={t('Japanese — largest first', '日本語 — 大きい順')}
          caption={t('top → bottom', '上 → 下')}
          parts={JA_PARTS}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PartList
          heading={t('English — smallest first', '英語 — 小さい順')}
          caption={t('top → bottom (reversed)', '上 → 下（逆順）')}
          parts={EN_PARTS}
        />
      </Grid>
    </Grid>

    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1, color: 'text.secondary' }}>
      <SwapVert fontSize="small" />
      <Typography variant="caption" sx={{ ml: 0.5 }}>
        {t('same address, reversed order', '同じ住所・順序が逆')}
      </Typography>
    </Box>

    {/* Worked example — a real 100-0001 result, deliberately darker than the
        page so it reads as a sample rather than a live result. */}
    <Paper
      elevation={0}
      sx={{ p: 3, mt: 1, bgcolor: '#0a0a0a', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Chip size="small" label={t('Example', '例')} color="secondary" variant="outlined" />
        <Typography variant="body2" color="text.secondary">
          〒{formatPostal(EXAMPLE.postal_code)} ·{' '}
          {t('1 entry · Address', '1 件 · 住所')}
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary">{t('Japanese', '日本語')}</Typography>
      <Typography variant="body1" gutterBottom>
        {japaneseFull(EXAMPLE, EXAMPLE.postal_code)}{' '}
        <Box component="span" sx={{ fontStyle: 'italic', color: 'primary.main' }}>{PLACEHOLDER_JA}</Box>
      </Typography>

      <Typography variant="caption" color="text.secondary">{t('English', '英語')}</Typography>
      <Typography variant="body1">
        <Box component="span" sx={{ fontStyle: 'italic', color: 'primary.main' }}>{PLACEHOLDER_EN}</Box>{' '}
        {englishFull(EXAMPLE, EXAMPLE.postal_code)}
      </Typography>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        {t(
          'One postal code can return several entries. Most are ordinary addresses; some are 大口事業所 (large-volume business) records that also carry a company name as part of the address.',
          '1つの郵便番号で複数の結果が返ることがあります。多くは通常の住所ですが、一部は大口事業所（だいぐちじぎょうしょ）の記録で、会社名も住所の一部として含まれます。'
        )}
      </Typography>
    </Paper>
  </Box>
);

export default AddressGuide;
